import React, { useState, useEffect } from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import { Box, Container, CircularProgress, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import RoadmapSection from './RoadmapSection'
import Horizon10Section from './Horizon10Section'
import IdeaDetailDrawer from './IdeaDetailDrawer'
import RolloverModal from './modals/RolloverModal'
import { roadmapAPI } from './api/roadmapAPI'
import TransitionScreen from './workflows/TransitionScreen'
import AddIdeaPopup from './workflows/AddIdeaPopup'
import TimeProjectionPopup from './workflows/TimeProjectionPopup'
import EngagementPopup from './workflows/EngagementPopup'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const RoadmapCockpit = ({
  onStartWorkflow,
  skipTransition = false,
  openAddIdeaFromUrl = false,
  onAddIdeaUrlConsumed = null,
}) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [ideasByHorizon, setIdeasByHorizon] = useState({
    dreams: [],
    this_year: [],
    next_year: [],
    year_5: [],
    year_10: [],
    completed: [],
  })
  const [loading, setLoading] = useState(true)
  const [selectedIdea, setSelectedIdea] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [rolloverSession, setRolloverSession] = useState(null)
  const [showRolloverModal, setShowRolloverModal] = useState(false)

  // Workflow states
  const [showTransition, setShowTransition] = useState(!skipTransition)
  const [showAddIdea, setShowAddIdea] = useState(false)
  const [showTimeProjection, setShowTimeProjection] = useState(false)
  const [showEngagement, setShowEngagement] = useState(false)
  const [currentIdeaData, setCurrentIdeaData] = useState(null)
  const [currentHorizon, setCurrentHorizon] = useState(null)

  const currentYear = new Date().getFullYear()

  useEffect(() => {
    fetchIdeas()
    checkRollover()
  }, [])

  useEffect(() => {
    if (!openAddIdeaFromUrl || loading) return

    setShowTransition(false)
    setCurrentHorizon(null)
    setShowAddIdea(true)
    if (onAddIdeaUrlConsumed) onAddIdeaUrlConsumed()
  }, [openAddIdeaFromUrl, loading, onAddIdeaUrlConsumed])

  const fetchIdeas = async () => {
    try {
      setLoading(true)
      const data = await roadmapAPI.getIdeasByHorizon()
      setIdeasByHorizon(data)
    } catch (error) {
      toast.error(t('roadmap.toastErrIdeasLoad'))
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const checkRollover = async () => {
    try {
      const data = await roadmapAPI.checkRollover()
      if (data.pending && data.id) {
        setRolloverSession(data)
        setShowRolloverModal(true)
      }
    } catch (error) {
      console.error('Error checking rollover:', error)
    }
  }

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result

    if (!destination) return

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }

    // Map droppableId to horizon
    const horizonMap = {
      dreams: 'DREAMS',
      this_year: 'YEAR_CURRENT',
      next_year: 'YEAR_NEXT',
      year_5: 'YEAR_5',
      year_10: 'YEAR_10',
      completed: 'COMPLETED',
    }

    const newHorizon = horizonMap[destination.droppableId]

    if (!newHorizon) return

    try {
      // Optimistic update
      const sourceHorizon = horizonMap[source.droppableId]
      const sourceList = ideasByHorizon[source.droppableId] || []
      const destList = ideasByHorizon[destination.droppableId] || []
      const idea = sourceList.find((i) => i.id === draggableId)

      if (!idea) return

      // Optimistic update - update local state immediately
      const optimisticIdea = {
        ...idea,
        horizon: newHorizon,
        planned_year: idea.planned_year, // Will be updated by backend
      }
      
      const optimisticSourceList = Array.from(sourceList)
      optimisticSourceList.splice(source.index, 1)
      const optimisticDestList = Array.from(destList)
      optimisticDestList.splice(destination.index, 0, optimisticIdea)

      setIdeasByHorizon({
        ...ideasByHorizon,
        [source.droppableId]: optimisticSourceList,
        [destination.droppableId]: optimisticDestList,
      })

      // Update backend
      await roadmapAPI.moveHorizon(draggableId, newHorizon)
      
      // Refresh the idea to get updated status and all latest data
      const refreshedIdea = await roadmapAPI.getIdea(draggableId)
      
      // Update local state with refreshed data (including new status)
      const finalSourceList = Array.from(sourceList)
      finalSourceList.splice(source.index, 1)
      const finalDestList = Array.from(destList)
      finalDestList.splice(destination.index, 0, refreshedIdea)

      setIdeasByHorizon({
        ...ideasByHorizon,
        [source.droppableId]: finalSourceList,
        [destination.droppableId]: finalDestList,
      })
      
      // If the drawer is open for this idea, refresh it with updated data from backend
      if (selectedIdea && selectedIdea.id === draggableId) {
        setSelectedIdea(refreshedIdea)
      }
      
      toast.success(t('roadmap.toastIdeaMoved'))
    } catch (error) {
      toast.error(t('roadmap.toastErrIdeaMove'))
      // Revert on error
      fetchIdeas()
    }
  }

  const handleIdeaClick = (idea) => {
    // Close drawer first if open to avoid conflicts
    if (drawerOpen) {
      setDrawerOpen(false)
      setSelectedIdea(null)
      // Small delay before opening new drawer
      setTimeout(() => {
        setSelectedIdea(idea)
        setDrawerOpen(true)
      }, 100)
    } else {
      setSelectedIdea(idea)
      setDrawerOpen(true)
    }
  }

  const handleAddIdea = (horizon = null) => {
    if (horizon) {
      // Direct add to specific horizon (for This Year/Next Year)
      setCurrentHorizon(horizon)
      setShowAddIdea(true)
    } else {
      // Full workflow
      if (!skipTransition) {
        setShowTransition(true)
      } else {
        setShowAddIdea(true)
      }
    }
  }

  const handleTransitionStart = () => {
    setShowTransition(false)
    setShowAddIdea(true)
  }

  const handleAddIdeaSubmit = (data) => {
    setCurrentIdeaData(data)
    setShowAddIdea(false)
    setShowTimeProjection(true)
  }

  const handleTimeProjectionSubmit = async (horizon) => {
    try {
      const ideaData = {
        ...currentIdeaData,
        horizon: horizon || currentHorizon || 'DREAMS',
      }

      const newIdea = await roadmapAPI.createIdea(ideaData)
      toast.success(t('roadmap.toastIdeaCreated'))

      // Refresh ideas
      await fetchIdeas()

      // Show engagement if needed
      if (horizon === 'YEAR_CURRENT' || horizon === 'YEAR_NEXT') {
        setCurrentIdeaData(newIdea)
        setShowEngagement(true)
      } else {
        // Reset workflow
        setShowTimeProjection(false)
        setCurrentIdeaData(null)
        setCurrentHorizon(null)
      }
    } catch (error) {
      toast.error(t('roadmap.toastErrIdeaCreate'))
      console.error(error)
    }
  }

  const handleEngagementClose = () => {
    setShowEngagement(false)
    setShowTimeProjection(false)
    setCurrentIdeaData(null)
    setCurrentHorizon(null)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#00001d',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        backgroundImage: 'url(/image%20full.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'relative',
        py: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <DragDropContext 
          onDragEnd={handleDragEnd}
          key={`drag-context-${drawerOpen ? 'open' : 'closed'}-${selectedIdea?.id || 'none'}`}
        >
          <RoadmapSection
            title={t('roadmap.cockpitDreamsTitle')}
            helperText={t('roadmap.cockpitDreamsHelper')}
            horizon="DREAMS"
            ideas={ideasByHorizon.dreams || []}
            onAdd={() => handleAddIdea()}
            onIdeaClick={handleIdeaClick}
            droppableId="dreams"
          />

          <RoadmapSection
            title={t('roadmap.cockpitYearCurrentTitle', { year: currentYear })}
            helperText={t('roadmap.cockpitYearCurrentHelper')}
            horizon="YEAR_CURRENT"
            ideas={ideasByHorizon.this_year || []}
            onAdd={() => handleAddIdea('YEAR_CURRENT')}
            onIdeaClick={handleIdeaClick}
            droppableId="this_year"
          />

          <RoadmapSection
            title={t('roadmap.cockpitYearNextTitle', { year: currentYear + 1 })}
            helperText={t('roadmap.cockpitYearNextHelper')}
            horizon="YEAR_NEXT"
            ideas={ideasByHorizon.next_year || []}
            onAdd={() => handleAddIdea('YEAR_NEXT')}
            onIdeaClick={handleIdeaClick}
            droppableId="next_year"
          />

          <RoadmapSection
            title={t('roadmap.cockpitYear5Title', { year: currentYear + 5 })}
            helperText={t('roadmap.cockpitYear5Helper')}
            horizon="YEAR_5"
            ideas={ideasByHorizon.year_5 || []}
            onAdd={() => handleAddIdea()}
            onIdeaClick={handleIdeaClick}
            droppableId="year_5"
          />

          <Horizon10Section
            currentYear={currentYear}
            ideas={ideasByHorizon.year_10 || []}
            onIdeaClick={handleIdeaClick}
            onUpdate={fetchIdeas}
          />

          <RoadmapSection
            title={t('roadmap.cockpitCompletedTitle')}
            helperText={t('roadmap.cockpitCompletedHelper')}
            horizon="COMPLETED"
            ideas={ideasByHorizon.completed || []}
            onIdeaClick={handleIdeaClick}
            droppableId="completed"
            collapsible
            defaultExpanded={false}
          />
        </DragDropContext>
      </Container>

      {/* Workflow Modals */}
      <TransitionScreen
        open={showTransition}
        onStart={handleTransitionStart}
      />

      <AddIdeaPopup
        open={showAddIdea}
        onClose={() => {
          setShowAddIdea(false)
          setCurrentHorizon(null)
        }}
        onSubmit={handleAddIdeaSubmit}
        onBack={showTransition ? null : () => setShowAddIdea(false)}
      />

      <TimeProjectionPopup
        open={showTimeProjection}
        onClose={() => {
          setShowTimeProjection(false)
          setCurrentIdeaData(null)
        }}
        onSubmit={handleTimeProjectionSubmit}
        onBack={() => {
          setShowTimeProjection(false)
          setShowAddIdea(true)
        }}
      />

      <EngagementPopup
        open={showEngagement}
        onClose={handleEngagementClose}
        ideaId={currentIdeaData?.id}
        onLinkCreated={() => {
          fetchIdeas()
          handleEngagementClose()
        }}
      />

      {/* Detail Drawer */}
      {selectedIdea && (
        <IdeaDetailDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false)
            // Delay clearing selected idea to allow drawer to close smoothly
            setTimeout(() => {
              setSelectedIdea(null)
            }, 300)
          }}
          idea={selectedIdea}
          onUpdate={async () => {
            // Refresh the selected idea data to get updated links
            try {
              const refreshedIdea = await roadmapAPI.getIdea(selectedIdea.id)
              setSelectedIdea(refreshedIdea)
            } catch (error) {
              console.error('Error refreshing idea:', error)
            }
            // Delay fetch to ensure drawer is fully closed and DOM is cleaned up
            setTimeout(() => {
              fetchIdeas()
            }, 500)
          }}
          onOpenIdea={(ideaToOpen) => {
            // Open another idea in the drawer
            setSelectedIdea(ideaToOpen)
            setDrawerOpen(true)
          }}
        />
      )}

      {/* Rollover Modal */}
      <RolloverModal
        open={showRolloverModal}
        onClose={() => setShowRolloverModal(false)}
        session={rolloverSession}
        onComplete={() => {
          setShowRolloverModal(false)
          fetchIdeas()
        }}
      />
    </Box>
  )
}

export default RoadmapCockpit
